import { useCallback, useEffect, useState } from 'react';
import { doc, getDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../../utils/init-firebase';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Per-user "which tours has this user already seen" state, persisted on the
 * Firestore user doc (users/{uid}.seenTours) — same home as
 * seenFeaturesByVersion and crmNotificationSettings.
 *
 * Shape:
 *   seenTours: {
 *     [tourId]: { version: number, status: 'completed' | 'dismissed', seenAt: ISOString }
 *   }
 *
 * Tour IDs are used as Firestore field paths, so they must NOT contain dots.
 * Writes are optimistic: local state updates immediately so a tour never
 * re-triggers in-session even if the network write is slow or fails.
 */
export function useSeenTours() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;

  const [seenTours, setSeenTours] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setSeenTours({});
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (!cancelled) {
          setSeenTours((snap.exists() && snap.data().seenTours) || {});
        }
      } catch (error) {
        console.error('FeatureTour: failed to load seenTours', error);
        if (!cancelled) setSeenTours({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const markTourSeen = useCallback(
    async (tourId, version, status) => {
      const entry = {
        version: version || 1,
        status: status || 'completed',
        seenAt: new Date().toISOString(),
      };
      setSeenTours((current) => ({ ...current, [tourId]: entry }));
      if (!uid) return;
      try {
        await updateDoc(doc(db, 'users', uid), { [`seenTours.${tourId}`]: entry });
      } catch (error) {
        console.error('FeatureTour: failed to persist seenTours', error);
      }
    },
    [uid]
  );

  /** Remove the seen flag so the tour auto-starts again (dev/testing). */
  const resetTour = useCallback(
    async (tourId) => {
      setSeenTours((current) => {
        const nextState = { ...current };
        delete nextState[tourId];
        return nextState;
      });
      if (!uid) return;
      try {
        await updateDoc(doc(db, 'users', uid), { [`seenTours.${tourId}`]: deleteField() });
      } catch (error) {
        console.error('FeatureTour: failed to reset tour', error);
      }
    },
    [uid]
  );

  return { seenTours, loading, markTourSeen, resetTour };
}
